import {
  ONBOARDING_FORM_FIELDS, ONBOARDING_TO_UNIFIED, cleanInfoAnswer, trustedContactPayload, unifiedQuestionById,
  validateUnifiedValue, type OnboardingClientFormSection, type OnboardingFormSection,
} from '@steward-app/steward-types';
import {
  emptyWire, mergeStale, viewToWire, wireList, type WireAnswers, type WireItem, type WireSave, type WireValue,
} from '../../intake/snapshotAnswers';
import {
  getOnboardingRecord, isStaleSave, saveOnboardingRecordAnswers, type OnboardingRecordView,
} from '../../api/onboardingRecord';
import type { Values } from './reviewAnswers';

/**
 * The onboarding form's answers as the unified client record holds them
 * (docs/43 §3.2, crossmarc/steward-app#2699 slice 4c), and the conversion to and from the flat
 * `Values` the form already speaks (`client_firstName`, `children_names`, ...).
 *
 * The form keeps its section model and its `Values`. This file is the one place that knows the
 * record's shape, as `intake/snapshotAnswers.ts` is for the snapshot, and it reuses that file's
 * list and merge machinery so the two forms cannot drift apart.
 *
 * ## One save is the whole form
 *
 * A record save is a form-scoped REPLACE (appcore `client-record-repository.ts`, read 2026-11-01):
 * a question placed on the onboarding form and ABSENT from the body is cleared, and a list's rows
 * ARE the list. The legacy routes saved one section at a time; here every save carries every
 * placed answer. {@link valuesToWire} starts from what the record held at the last read and
 * overwrites only the sections the page manages, so what the form does not show (a section the
 * firm did not ask for, a question placed on this form that the page never draws) is carried
 * through unchanged and never cleared by a save.
 *
 * ## What is not here
 *
 * The tax id, full account numbers, uploads and the information-request answers (the `requests`
 * section) stay on the legacy `/me/onboarding-forms/...` routes; `requests.*` lives on a row of
 * its own with its own version, which a record save never carries.
 *
 * ## Children
 *
 * The form holds children as two index-aligned lists (`children_names`, `children_dateOfBirths`);
 * the record holds one list, `spousePartner.children`, a row per child. Row ids come from
 * {@link assignItemIds}, so a child keeps the id (and the fields another form or a firm wrote on
 * that row) across saves.
 */

const CHILDREN_LIST = 'spousePartner.children';
const CHILD_NAME_ID = ONBOARDING_TO_UNIFIED['children_names']!;
const CHILD_DOB_ID = ONBOARDING_TO_UNIFIED['children_dateOfBirths']!;

/** Unified id to the form's flat key, for every scalar the form draws. */
const KEY_OF_ID: ReadonlyMap<string, string> = new Map(
  Object.entries(ONBOARDING_TO_UNIFIED).filter(([key]) => key !== 'children_names' && key !== 'children_dateOfBirths').map(([k, id]) => [id, k]),
);

/**
 * The values `section` would send: its declared keys only, blanks left out (a blank clears).
 * A `stringList` field (children's names and dates of birth) is included whenever at least one
 * item in it is non-blank, so the two children lists stay aligned index for index; an all-blank
 * list is left out, same as an all-blank text field (crossmarc/steward-app#1812).
 */
export function sectionPayload(section: OnboardingFormSection, values: Values): Record<string, string | boolean | string[]> {
  const out: Record<string, string | boolean | string[]> = {};
  for (const { key, kind } of ONBOARDING_FORM_FIELDS[section]) {
    const v = values[key];
    if (typeof v === 'boolean') out[key] = v;
    else if (typeof v === 'string' && v.trim() !== '') out[key] = v.trim();
    else if (kind === 'stringList' && Array.isArray(v)) {
      const trimmed = v.map((x) => (typeof x === 'string' ? x.trim() : ''));
      if (trimmed.some((x) => x !== '')) out[key] = trimmed;
    }
  }
  // A typed answer is cleaned the way the server and the firm's view clean it (steward-app#2284).
  if (section === 'requests' && Array.isArray(out['requests_answerTexts'])) {
    out['requests_answerTexts'] = out['requests_answerTexts'].map((t) => cleanInfoAnswer(t));
  }
  // A chosen family member makes some free-text keys stale, and the choice itself is kept only
  // while it still holds (crossmarc/steward-app#1954): see `trustedContactPayload`.
  return section === 'trustedContact' ? trustedContactPayload(out, values) : out;
}

/**
 * The page's answers as the whole-form record body, and what was HELD BACK instead of sent (a
 * value the record's validator refuses, or more children than the record admits). A held value
 * stays at what the record last held, never left out, because leaving it out would clear it.
 * A non-empty `heldBack` means the record does not hold what the page shows, so the caller must
 * not report the save as saved.
 *
 * `view` is the page's `Values` as the form can stand behind them (`answersView`), `stored` the
 * record as the page last adopted it, and `sections` the client form sections the page shows.
 */
export function valuesToWire(view: Values, stored: WireAnswers | null, sections: readonly OnboardingClientFormSection[]): WireSave {
  const base = stored ?? emptyWire();
  const wire: WireAnswers = { answers: { ...base.answers }, lists: { ...base.lists } };
  const heldBack = new Set<string>();

  for (const section of sections) {
    // Everything this section draws is replaced by the page: clear it first, then lay the answers on.
    for (const { key, kind } of ONBOARDING_FORM_FIELDS[section]) {
      if (kind === 'stringList') continue;
      const id = ONBOARDING_TO_UNIFIED[key];
      if (id !== undefined) delete wire.answers[id];
    }
    const payload = sectionPayload(section, view);
    for (const [key, value] of Object.entries(payload)) {
      if (key === 'children_names' || key === 'children_dateOfBirths') continue;
      const id = ONBOARDING_TO_UNIFIED[key];
      if (id === undefined) continue;
      if (validateUnifiedValue(id, value).ok) wire.answers[id] = value;
      else {
        heldBack.add(id);
        if (id in base.answers) wire.answers[id] = base.answers[id]!;
      }
    }
    if (section === 'children') childrenToWire(payload, base, wire, heldBack);
  }
  return { wire, heldBack: [...heldBack] };
}

function childrenToWire(
  payload: Record<string, string | boolean | string[]>, base: WireAnswers, wire: WireAnswers, heldBack: Set<string>,
): void {
  const names = Array.isArray(payload['children_names']) ? payload['children_names'] : [];
  const dobs = Array.isArray(payload['children_dateOfBirths']) ? payload['children_dateOfBirths'] : [];
  const count = Math.max(names.length, dobs.length);
  const rows: Record<string, WireValue>[] = [];
  for (let i = 0; i < count; i += 1) {
    const fields: Record<string, WireValue> = {};
    if ((names[i] ?? '') !== '') fields[CHILD_NAME_ID] = names[i]!;
    if ((dobs[i] ?? '') !== '') fields[CHILD_DOB_ID] = dobs[i]!;
    if (Object.keys(fields).length > 0) rows.push(fields);
  }
  const stored = base.lists[CHILDREN_LIST] ?? [];
  const max = unifiedQuestionById(CHILDREN_LIST)?.maxItems;
  if (max !== undefined && rows.length > max) {
    // More children than the record admits. A prefix would make the replace delete the rest.
    heldBack.add(CHILDREN_LIST);
    if (stored.length > 0) wire.lists[CHILDREN_LIST] = stored; else delete wire.lists[CHILDREN_LIST];
    return;
  }
  const items = wireList(rows, rows.map(() => undefined), stored, heldBack);
  if (items.length > 0) wire.lists[CHILDREN_LIST] = items; else delete wire.lists[CHILDREN_LIST];
}

/** The record to the flat values the form edits. The inverse of {@link valuesToWire} for what the form draws. */
export function wireToValues(wire: WireAnswers): Values {
  const values: Values = {};
  for (const [id, value] of Object.entries(wire.answers)) {
    const key = KEY_OF_ID.get(id);
    if (key !== undefined) values[key] = value;
  }
  const kids: WireItem[] = wire.lists[CHILDREN_LIST] ?? [];
  if (kids.length > 0) {
    const text = (v: WireValue | undefined): string => (typeof v === 'string' ? v : '');
    values['children_names'] = kids.map((k) => text(k.fields[CHILD_NAME_ID]));
    values['children_dateOfBirths'] = kids.map((k) => text(k.fields[CHILD_DOB_ID]));
  }
  return values;
}

/** The record the server returned, as the wire copy the page's next save is based on. */
export const recordToWire = (view: OnboardingRecordView): WireAnswers => viewToWire(view.answers, 'onboarding');

/**
 * The form keys whose current value the CLIENT did not give on this form: the snapshot, or a
 * firm, wrote it. The form says "Check what we filled in and add the rest" while one is on screen.
 */
export function filledInKeys(view: OnboardingRecordView): Set<string> {
  const out = new Set<string>();
  for (const [id, a] of Object.entries(view.answers.answers)) {
    const key = KEY_OF_ID.get(id);
    const via = typeof a === 'object' && a !== null ? (a as Record<string, unknown>)['via'] : undefined;
    if (key !== undefined && via !== 'onboarding') out.add(key);
  }
  for (const item of view.answers.lists[CHILDREN_LIST] ?? []) {
    for (const a of Object.values(item.fields)) {
      const via = typeof a === 'object' && a !== null ? (a as Record<string, unknown>)['via'] : undefined;
      if (via !== 'onboarding') { out.add('children_names'); out.add('children_dateOfBirths'); }
    }
  }
  return out;
}

/**
 * The form keys whose current answer the client's advisor entered (`via = staff`, docs/43 §4,
 * crossmarc/steward-app#2538), each with the newest staff `at`. Children are one mark, on
 * `children_names`. The read carries no `by`, so neither does this.
 */
export function staffKeysOf(view: OnboardingRecordView): Map<string, string> {
  const out = new Map<string, string>();
  const put = (key: string, at: string): void => { const was = out.get(key); if (was === undefined || at > was) out.set(key, at); };
  const staffAt = (a: unknown): string | undefined => {
    const r = typeof a === 'object' && a !== null ? a as Record<string, unknown> : {};
    return r['via'] === 'staff' && typeof r['at'] === 'string' ? r['at'] : undefined;
  };
  for (const [id, a] of Object.entries(view.answers.answers)) {
    const key = KEY_OF_ID.get(id);
    const at = staffAt(a);
    if (key !== undefined && at !== undefined) put(key, at);
  }
  for (const item of view.answers.lists[CHILDREN_LIST] ?? []) {
    for (const a of Object.values(item.fields)) { const at = staffAt(a); if (at !== undefined) put('children_names', at); }
  }
  return out;
}

/** What the page holds of the record: the version it was read at and the copy that version describes. */
export interface RecordBase { version: number; stored: WireAnswers }

export const baseOf = (view: OnboardingRecordView): RecordBase => ({ version: view.answers._v, stored: recordToWire(view) });

export type RecordSave =
  | { kind: 'saved'; base: RecordBase }
  /** The record had moved: `merged` is the person's edits laid over the server's copy, now saved. */
  | { kind: 'merged'; base: RecordBase; merged: WireAnswers };

/**
 * Saves the whole form against the version the page holds. A save the server finds stale (409
 * `stale_save`) is mended once: the record is read again, the person's unsaved edits are laid
 * over it ({@link mergeStale}, three-way against the copy the page is built on), and the merge is
 * sent against the version just read. Any other failure, and a second stale, throws the
 * `ApiError` and leaves the caller's base where it was, so the next save merges again against the
 * copy the page really has. The caller decides whether the page takes a `merged` result.
 */
export async function saveRecord(token: string, firmSlug: string, wire: WireAnswers, base: RecordBase): Promise<RecordSave> {
  try {
    const res = await saveOnboardingRecordAnswers(token, firmSlug, { expectedVersion: base.version, answers: wire.answers, lists: wire.lists });
    return { kind: 'saved', base: { version: typeof res.version === 'number' ? res.version : base.version, stored: wire } };
  } catch (err) {
    if (!isStaleSave(err)) throw err;
  }
  const fresh = await getOnboardingRecord(token, firmSlug);
  const merged = mergeStale(wire, base.stored, recordToWire(fresh));
  const res = await saveOnboardingRecordAnswers(token, firmSlug, { expectedVersion: fresh.answers._v, answers: merged.answers, lists: merged.lists });
  return { kind: 'merged', merged, base: { version: typeof res.version === 'number' ? res.version : fresh.answers._v, stored: merged } };
}

